import { type RefObject, useEffect, useRef } from 'react';

/**
 * How long a widget has to stay on screen before it counts as seen.
 *
 * Without a dwell requirement, scrolling past a long page would report every slot
 * on it as viewed. One second is short enough that a deliberate glance counts and
 * long enough that a scroll-by does not.
 */
const MIN_VISIBLE_MS_FOR_VIEW = 1_000;

export interface UseWidgetVisibilityOptions {
    /** The element wrapping the widget frame. */
    ref: RefObject<HTMLElement | null>;
    /**
     * Whether the widget is currently worth measuring — in practice, whether it is
     * rendering rather than loading or broken. A slot showing a spinner is on
     * screen, but nobody has seen the widget.
     */
    enabled: boolean;
    /**
     * Identifies the widget instance. Changing it resets the measurement and
     * flushes whatever the previous instance accumulated, so a session recovery
     * that swaps the frame does not blend two documents' dwell together.
     */
    instanceKey: string;
    /** Called at most once per instance, when sustained visibility is first reached. */
    onViewed: () => void;
    /**
     * Called at most once per instance with the total time the widget spent on
     * screen — only for an instance that was viewed at all.
     *
     * Best-effort by nature: it fires on teardown and on `pagehide`, and a browser
     * killed outright reports nothing. Dwell is a distribution, not an audit trail.
     */
    onHidden: (visibleMs: number) => void;
}

/**
 * Measures whether a widget was actually seen, and for how long.
 *
 * Visibility is any intersection with the viewport rather than a ratio threshold:
 * a widget taller than the viewport can never reach 50% visible, and those are
 * exactly the full-page widgets most worth counting.
 *
 * Time spent with the tab backgrounded does not count. Without that, a slot left
 * open in an unfocused tab would report hours of engagement.
 */
export function useWidgetVisibility({
    ref,
    enabled,
    instanceKey,
    onViewed,
    onHidden,
}: UseWidgetVisibilityOptions): void {
    // Read the callbacks through refs so a host passing fresh function identities
    // every render cannot tear down and re-arm the observer underneath us.
    const onViewedRef = useRef(onViewed);
    onViewedRef.current = onViewed;
    const onHiddenRef = useRef(onHidden);
    onHiddenRef.current = onHidden;

    // Monotonic start of the current visible period, or undefined while hidden.
    const visibleSinceRef = useRef<number>(undefined);
    const accumulatedMsRef = useRef(0);
    const viewedRef = useRef(false);
    const viewTimerRef = useRef<number>(undefined);
    // Tracks the observer's own view of intersection, which outlives an `enabled`
    // flip: a widget that recovers from an expired session is still on screen, and
    // should resume accumulating without waiting for another scroll event.
    const intersectingRef = useRef(false);
    const measuredInstanceRef = useRef<string>(undefined);

    useEffect(() => {
        // Reset here rather than in an effect of its own. React runs every pending
        // cleanup before any setup, so the previous instance's dwell has already
        // been flushed by the cleanup below — but a *separate* reset effect would
        // run AFTER this one's setup and clear the flags it had just armed, leaving
        // the new instance permanently unmeasured. Guarded on the key so an
        // `enabled` flip, which re-runs this same effect, keeps its accumulation.
        if (measuredInstanceRef.current !== instanceKey) {
            measuredInstanceRef.current = instanceKey;
            viewedRef.current = false;
            accumulatedMsRef.current = 0;
            visibleSinceRef.current = undefined;
        }

        const element = ref.current;
        // jsdom has no IntersectionObserver. Reporting nothing is the honest
        // degradation — treating an unobservable widget as viewed would quietly
        // turn the adoption metric into a render count.
        if (element === null || typeof IntersectionObserver === 'undefined') {
            return;
        }

        const stopVisible = () => {
            if (viewTimerRef.current !== undefined) {
                window.clearTimeout(viewTimerRef.current);
                viewTimerRef.current = undefined;
            }
            if (visibleSinceRef.current !== undefined) {
                accumulatedMsRef.current += performance.now() - visibleSinceRef.current;
                visibleSinceRef.current = undefined;
            }
        };

        const startVisible = () => {
            if (visibleSinceRef.current !== undefined) {
                return;
            }
            visibleSinceRef.current = performance.now();
            if (viewedRef.current) {
                return;
            }
            viewTimerRef.current = window.setTimeout(() => {
                viewTimerRef.current = undefined;
                viewedRef.current = true;
                onViewedRef.current();
            }, MIN_VISIBLE_MS_FOR_VIEW);
        };

        // Every condition that has to hold for the widget to be "on screen and worth
        // counting", in one place — so the observer, the `enabled` flip and the tab
        // switch cannot disagree about what visible means.
        const sync = () => {
            if (enabled && intersectingRef.current && document.visibilityState === 'visible') {
                startVisible();
            } else {
                stopVisible();
            }
        };

        // Flushes independently of effect-cleanup ordering: it stops the in-flight
        // period itself rather than relying on another cleanup having run first.
        const flush = () => {
            stopVisible();
            const total = accumulatedMsRef.current;
            accumulatedMsRef.current = 0;
            if (viewedRef.current && total > 0) {
                onHiddenRef.current(Math.round(total));
            }
        };

        const observer = new IntersectionObserver((entries) => {
            const latest = entries.at(-1);
            if (latest === undefined) {
                return;
            }
            intersectingRef.current = latest.isIntersecting;
            sync();
        });
        observer.observe(element);
        document.addEventListener('visibilitychange', sync);
        // The last chance to report dwell for a user who navigates away or closes
        // the tab. `pagehide` rather than `unload`, which browsers no longer fire
        // reliably and which disqualifies the page from the back/forward cache.
        window.addEventListener('pagehide', flush);
        sync();

        return () => {
            observer.disconnect();
            document.removeEventListener('visibilitychange', sync);
            window.removeEventListener('pagehide', flush);
            flush();
        };
    }, [enabled, instanceKey, ref]);
}
